import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ActionButton, Buttons } from "../../components/Buttons";
import { MdOutlineDelete } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import { HiOutlineDotsVertical } from "react-icons/hi";
import {
  fetchAreas,
  addArea,
  updateArea,
  deleteArea,
} from "../../slice/AreaSlice";
import PageTitle from "../../components/PageTitle";
import CustomModal from "../../components/Modal";
import NotifyData from "../../components/NotifyData";
import AreaCreation from "../creations/AreaCreation";
import { TextInputform } from "../../components/Forms";
import TableUI from "../../components/TableUI";
import Pagination from "../../components/Pagination";

const Area = () => {
  const dispatch = useDispatch();
  const { areas } = useSelector((state) => state.areas);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;

  const areaSchema = [
    { name: "area_name", label: "Area Name", type: "text", required: true },
    { name: "area_prefix", label: "Area Prefix", type: "text", required: true },
  ];

  const [formData, setFormData] = useState({ area_name: "", area_prefix: "" });
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchAreas(searchTerm));
  }, [dispatch, searchTerm]);

  const handleOpen = () => setShow(true);
  const handleClose = () => setShow(false);

  const handleEdit = (area) => {
    setEditMode(true);
    setSelectedArea(area || {});
    setFormData({
      area_name: area?.area_name || "",
      area_prefix: area?.area_prefix || "",
    });
    handleOpen();
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({ area_name: "", area_prefix: "" });
    handleOpen();
  };
  const handleSubmit = async () => {
    if (!formData.area_name || !formData.area_prefix) {
      NotifyData("Area Creation Failed: All fields are required", "error");
      return;
    }
    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const msg = await dispatch(
        addArea({ ...formData, current_user_id: currentUserId })
      ).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchAreas(searchTerm));
      handleClose();
      setFormData({ area_name: "", area_prefix: "" });
      setEditMode(false);
    } catch (error) {
      NotifyData(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.area_name || !formData.area_prefix) {
      NotifyData("Area Update Failed: All fields are required", "error");
      return;
    }
    if (!currentUserId || !selectedArea?.area_id) {
      NotifyData("Area Update Failed: Invalid data", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const msg = await dispatch(
        updateArea({
          ...formData,
          area_id: selectedArea.area_id,
          current_user_id: currentUserId,
        })
      ).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchAreas(searchTerm));
      handleClose();
      setFormData({ area_name: "", area_prefix: "" });
      setEditMode(false);
    } catch (error) {
      NotifyData(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (areaId) => {
    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }
    try {
      const msg = await dispatch(
        deleteArea({ areaId, currentUserId })
      ).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchAreas(searchTerm));
    } catch (error) {
      NotifyData(error.message, "error");
    }
  };

  // ✅ Search filter
  const filteredAreas =
    areas?.filter((area) =>
      area?.area_name?.toLowerCase()?.includes(searchTerm.toLowerCase())
    ) || [];

  // ✅ Pagination logic
  const totalRecords = filteredAreas.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = filteredAreas.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // ✅ Table headers
  const headers = ["No", "Area Name", "Area Prefix"];

  // ✅ Table body
  const body = currentData.map((area, index) => ({
    key: area.area_id,
    values: [
      startIndex + index + 1,
      area.area_name,
      area.area_prefix,
      <ActionButton
        options={[
          {
            label: "Edit",
            icon: <LiaEditSolid />,
            onClick: () => handleEdit(area),
          },
          {
            label: "Delete",
            icon: <MdOutlineDelete />,
            onClick: () => handleDelete(area.area_id),
          },
        ]}
        label={<HiOutlineDotsVertical />}
      />,
    ],
  }));

  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col xs="6" className="py-3">
            <PageTitle PageTitle="Area" showButton={false} />
          </Col>
          <Col xs="6" className="py-3 text-end">
            <Buttons
              btnlabel="Add New"
              className="add-btn"
              onClick={handleCreate}
            />
          </Col>

          {/* Search */}
          <Col xs="12" lg="3" className="py-3">
            <TextInputform
              textlabel="Search Areas"
              PlaceHolder="Search by area name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col xs="12" className="d-flex justify-content-end">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </Col>
          {/* ✅ Table with TableUI */}
          <Col xs="12" className="py-3 tableui">
            <TableUI
              headers={headers}
              body={body}
              showActionColumn={true}
              noRecordMessage="No areas found"
            />
          </Col>
        </Row>
      </Container>

      {/* Modal for Add/Edit */}
      <CustomModal
        show={show}
        setShow={setShow}
        pageTitle={editMode ? "Edit Area" : "Create Area"}
        showButton={true}
        submitButton={true}
        submitLabel={
          isSubmitting
            ? editMode
              ? "Updating..."
              : "Submitting..."
            : editMode
            ? "Update"
            : "Submit"
        }
        CancelLabel="Cancel"
        BodyComponent={
          <AreaCreation
            formData={formData}
            setFormData={setFormData}
            schema={areaSchema}
          />
        }
        OnClick={editMode ? handleUpdate : handleSubmit}
        Size="md"
        handleOpen={handleOpen}
        handleClose={handleClose}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Area;
